import type { LucideIcon } from "lucide-react"
import { Container } from "@/components/layout/Container"

export interface Feature {
	icon: LucideIcon
	title: string
	text: string
}

interface FeatureGridProps {
	title?: React.ReactNode
	subtitle?: string
	items: Feature[]
}

/**
 * Rejilla de beneficios: icono de Lucide (en color primary) + título + texto.
 * Sin tarjetas ni imágenes, para una lectura limpia. Mobile-first: 1 columna en
 * móvil, 2 en `sm`, 3 en `lg`.
 *
 * Reutilizable para "Beneficios", "Cómo funciona", "Por qué elegirnos", etc.
 */
export function FeatureGrid({ title, subtitle, items }: FeatureGridProps) {
	return (
		<section className="relative overflow-hidden bg-background">
			<Container className="relative py-20 md:py-28">
				{(title || subtitle) && (
					<div className="mx-auto max-w-3xl text-center">
						{title && (
							<>
								<span
									aria-hidden
									className="mb-12 block h-0.25 w-full bg-linear-to-r from-transparent via-foreground/50 to-transparent"
								/>
								<h2 className="text-balance text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
									{title}
								</h2>
								<span
									aria-hidden
									className="mt-12 block h-0.25 w-full bg-linear-to-r from-transparent via-foreground/50 to-transparent"
								/>
							</>
						)}
						{subtitle && <p className="mt-4 text-pretty text-lg text-muted-foreground">{subtitle}</p>}
					</div>
				)}

				<div className="mt-24 grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
					{items.map(({ icon: Icon, title: itemTitle, text }) => (
						<div key={itemTitle} className="text-center sm:text-left">
							<div className="inline-flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
								<Icon className="size-6" />
							</div>
							<h3 className="mt-4 text-lg font-semibold">{itemTitle}</h3>
							<p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
						</div>
					))}
				</div>
			</Container>
		</section>
	)
}
